/**
 * Merge Dialog Component
 * Opens a detail modal for merging selected pilot accounts.
 * The admin chooses a surviving Parent_User; all others become alias records.
 *
 * Requirements: 8.9, 8.10, 8.11, 8.17, 8.20
 */

import { openDetailModal } from './detail-modal.js';
import { mergePilots } from '../services/api.js';

/**
 * Build the descriptions object for a merge operation.
 *
 * For each pilot that is NOT the parent, uses the custom description if
 * provided, otherwise defaults to "Merged from <pilot.name>".
 *
 * Exported for property-based testing (Properties 13.4, 13.5, 13.6).
 *
 * @param {Object[]} selectedPilots - Array of pilot objects with { id, name }
 * @param {string} parentId - UUID of the chosen surviving parent
 * @param {Object<string, string>} [customDescriptions={}] - Custom descriptions keyed by pilot ID
 * @returns {Object<string, string>} Descriptions keyed by alias pilot ID
 */
export function buildMergeDescriptions(selectedPilots, parentId, customDescriptions = {}) {
  const descriptions = {};
  for (const pilot of selectedPilots) {
    if (pilot.id === parentId) continue;
    descriptions[pilot.id] =
      customDescriptions[pilot.id] != null && customDescriptions[pilot.id] !== ''
        ? customDescriptions[pilot.id]
        : `Merged from ${pilot.name}`;
  }
  return descriptions;
}

/**
 * Open the merge dialog modal for the given selected pilots.
 *
 * @param {Object[]} selectedPilots - Array of pilot objects with { id, name, email }
 * @param {Function} onComplete - Callback invoked after a successful merge (to refresh the grid)
 */
export function openMergeDialog(selectedPilots, onComplete) {
  // Track the currently selected parent ID and custom descriptions
  let selectedParentId = null;
  const customDescriptions = {};

  // Build the custom section DOM
  const section = document.createElement('div');
  section.className = 'merge-dialog';

  // Instructions
  const instructions = document.createElement('p');
  instructions.className = 'merge-dialog__instructions';
  instructions.textContent =
    'Choose the surviving parent user. All other selected users will become alias records pointing to the parent.';
  section.appendChild(instructions);

  // Pilot list with radio buttons and description fields
  const list = document.createElement('div');
  list.className = 'merge-dialog__pilot-list';

  for (const pilot of selectedPilots) {
    const row = document.createElement('div');
    row.className = 'merge-dialog__pilot-row';
    row.dataset.pilotId = pilot.id;

    // Radio button for parent selection
    const radioLabel = document.createElement('label');
    radioLabel.className = 'merge-dialog__radio-label';

    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = 'merge-parent';
    radio.value = pilot.id;
    radio.className = 'merge-dialog__radio';
    radio.addEventListener('change', () => {
      selectedParentId = pilot.id;
      updateDescriptionFields();
    });

    const nameSpan = document.createElement('span');
    nameSpan.className = 'merge-dialog__pilot-name';
    nameSpan.textContent = pilot.name;

    const emailSpan = document.createElement('span');
    emailSpan.className = 'merge-dialog__pilot-email';
    emailSpan.textContent = pilot.email ? ` (${pilot.email})` : '';

    radioLabel.appendChild(radio);
    radioLabel.appendChild(nameSpan);
    radioLabel.appendChild(emailSpan);
    row.appendChild(radioLabel);

    // Description field (hidden for the parent, shown for aliases)
    const descContainer = document.createElement('div');
    descContainer.className = 'merge-dialog__desc-container';
    descContainer.style.display = 'none';

    const descLabel = document.createElement('label');
    descLabel.className = 'merge-dialog__desc-label';
    descLabel.textContent = 'Alias description:';

    const descInput = document.createElement('input');
    descInput.type = 'text';
    descInput.className = 'merge-dialog__desc-input';
    descInput.placeholder = `Merged from ${pilot.name}`;
    descInput.dataset.pilotId = pilot.id;
    descInput.addEventListener('input', () => {
      customDescriptions[pilot.id] = descInput.value;
    });

    descContainer.appendChild(descLabel);
    descContainer.appendChild(descInput);
    row.appendChild(descContainer);

    list.appendChild(row);
  }

  section.appendChild(list);

  /**
   * Show/hide description fields based on the current parent selection.
   * The parent pilot does not get a description field; all others do.
   */
  function updateDescriptionFields() {
    const rows = list.querySelectorAll('.merge-dialog__pilot-row');
    for (const row of rows) {
      const pilotId = row.dataset.pilotId;
      const descContainer = row.querySelector('.merge-dialog__desc-container');
      if (pilotId === selectedParentId) {
        descContainer.style.display = 'none';
      } else {
        descContainer.style.display = '';
      }
    }
  }

  openDetailModal({
    title: 'Merge Users',
    fields: [],
    customSections: [section],
    saveLabel: 'Merge',
    onSave: async () => {
      // Validate that a parent is selected
      if (!selectedParentId) {
        throw new Error('Please select a surviving parent user');
      }

      // Collect alias IDs (all selected except the parent)
      const aliasIds = selectedPilots
        .filter((p) => p.id !== selectedParentId)
        .map((p) => p.id);

      // Build descriptions
      const descriptions = buildMergeDescriptions(selectedPilots, selectedParentId, customDescriptions);

      // Call the merge API
      await mergePilots(selectedParentId, aliasIds, descriptions);

      // Notify caller of success
      if (onComplete) {
        await onComplete();
      }
    },
  });
}
